import React, {useMemo, useState} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../components/DataTable";
import WorkgroupsAutocomplete from "../../components/inputs/WorkgroupsAutocomplete";
import FiltersBox from "../../components/FiltersBox";

import MDBox from "materialDashboard/components/MDBox";
import MDAvatar from "materialDashboard/components/MDAvatar";
import MDTypography from "materialDashboard/components/MDTypography";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import WorkgroupsCol from "../../components/columns/WorkgroupsCol";


const GroupsTable = (props) => {
    const {
        rows,
        page_count=-1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        sorting,
        onSortingChange,
        onDelete,
        dataTableId
    } = props;

    const {t} = useTranslation(["groups"]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                cell: props => <GroupCol row={props.row} />,
                header: () => <span>{t("table.group")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "20rem"
                }
            },
            {
                accessorKey: 'workgroups',
                cell: props => <WorkgroupsCol workgroups={props.row?.original?.workgroups} />,
                header: () => <span>{t("table.workgroups")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "20rem",
                    align: "center"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol row={props.row} onDelete={onDelete}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right"
                }
            },
        ],
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const defaultData = useMemo(() => [], [])

    const table = useReactTable({
        data: rows ?? defaultData,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
    })

    return <DataTable dataTableId={dataTableId}
                      table={table}
                      canSearch
                      showRowColoring={props.showRowColoring}
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange}/>}/>
}

const GroupCol = ({row}) => {
    const {name, workgroups} = row.original;

    return <MDBox display="flex" alignItems="center" lineHeight={1}>
        <MDAvatar src={null} name={name} size="sm" bgColor={"secondary"}/>
        <MDBox ml={2} lineHeight={1}>
            <MDTypography display="block" variant="button" fontWeight="medium">
                {name}
            </MDTypography>
            <MDTypography display="block" variant="caption">
                {workgroups.map(w => w.name).join(', ')}
            </MDTypography>
        </MDBox>
    </MDBox>;
}

const ActionCol = ({row, onDelete}) => {
    const navigate = useNavigate();
    const {id} = row.original;
    return <Stack direction="row" spacing={1} justifyContent="end">
        <IconButton onClick={() => navigate(`/groups/${id}`)}>
            <Icon fontSize="small">edit</Icon>
        </IconButton>
        <IconButton onClick={() => onDelete(id)}>
            <Icon fontSize="small">delete</Icon>
        </IconButton>
    </Stack>
}

const Filters = ({onChange}) => {
    const [selectedWorkgroups, setSelectedWorkgroups] = useState([]);

    const filters = {
        "workgroup__id__in": selectedWorkgroups.map(w => w.id).join(',')
    }

    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters}>
        <WorkgroupsAutocomplete selectedWorkgroups={selectedWorkgroups}
                                setSelectedWorkgroups={setSelectedWorkgroups}
                                workgroupFilters={{
                                    acl__users_read: true
                                }}
                                sx={{width: "24rem"}}/>
    </FiltersBox>
}

export default GroupsTable;